// src/components/profile/ExecutiveOverview/TrioCards.jsx
import React, { useState } from 'react';
import { Provenance } from '../../../utils/provenance';
import { ChevronDown, ChevronUp, Target, Lightbulb, TrendingUp } from 'lucide-react';

const Card = ({ title, bullets, accent, source, updatedAt, icon: Icon }) => {
  const [expanded, setExpanded] = useState(false);
  const displayBullets = expanded ? bullets : bullets.slice(0, 3);
  const hasMore = bullets.length > 3;
  
  return (
    <div className="group relative rounded-xl border border-gray-200 bg-white hover:border-gray-300 hover:shadow-lg transition-all duration-200 p-5 h-full flex flex-col overflow-hidden">
      {/* Top accent gradient */}
      <div 
        className="absolute top-0 left-0 right-0 h-1" 
        style={{ background: `linear-gradient(90deg, ${accent}, ${accent}dd)` }}
      />
      
      {/* Icon and Header */}
      <div className="flex items-start justify-between mb-4 mt-2">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-gray-400" />}
          <h3 className="text-xs font-bold uppercase tracking-wider text-gray-600">
            {title}
          </h3>
        </div>
        {source && (
          <Provenance 
            source={source} 
            updatedAt={updatedAt}
            className="opacity-40 group-hover:opacity-70 transition-opacity"
          />
        )}
      </div>
      
      {/* Content with improved spacing */}
      <div className={`flex-1 ${!expanded && hasMore ? 'relative' : ''}`}>
        <ul className="space-y-2.5">
          {displayBullets.map((bullet, i) => (
            <li key={i} className="flex items-start">
              <span 
                className="mr-2.5 mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0" 
                style={{ backgroundColor: accent }}
              />
              <span className="text-sm text-gray-700 leading-6 font-normal">
                {bullet}
              </span>
            </li>
          ))}
        </ul>
        
        {!expanded && hasMore && (
          <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white via-white/95 to-transparent" />
        )}
      </div>
      
      {hasMore && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="relative z-10 mt-3 flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-700 transition-colors"
        >
          <span>{expanded ? 'Show less' : `Show ${bullets.length - 3} more`}</span>
          {expanded ? (
            <ChevronUp className="w-3.5 h-3.5" />
          ) : (
            <ChevronDown className="w-3.5 h-3.5" />
          )}
        </button>
      )}
    </div>
  );
};

export default function TrioCards({ problem, solution, value }) {
  const safeData = {
    problem: {
      bullets: problem?.bullets || ['Analyzing market challenges'],
      source: problem?.source,
      updatedAt: problem?.updatedAt
    },
    solution: {
      bullets: solution?.bullets || ['Implementing data solutions'],
      source: solution?.source,
      updatedAt: solution?.updatedAt
    },
    value: {
      bullets: value?.bullets || ['Delivering business value'],
      source: value?.source,
      updatedAt: value?.updatedAt
    }
  };
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3"> 
      <Card 
        title="MARKET OPPORTUNITY" 
        bullets={safeData.problem.bullets} 
        accent="#DC2626"
        icon={Target}
        source={safeData.problem.source}
        updatedAt={safeData.problem.updatedAt}
      />
      <Card 
        title="DATA PRODUCT SOLUTION" 
        bullets={safeData.solution.bullets} 
        accent="#2563EB"
        icon={Lightbulb}
        source={safeData.solution.source}
        updatedAt={safeData.solution.updatedAt}
      />
      <Card 
        title="BUSINESS VALUE" 
        bullets={safeData.value.bullets} 
        accent="#059669"
        icon={TrendingUp}
        source={safeData.value.source}
        updatedAt={safeData.value.updatedAt}
      />
    </div>
  );
}